import { IDENTITY_API_BASE } from '../config/apiBase';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import './SignupPage.css';
import { useSubscriptionPlans } from '../hooks/useSubscriptionPlans';

const DEFAULT_PLAN = 'standard';

function SignupPage() {
  const { plans, loading: plansLoading } = useSubscriptionPlans();
  const [searchParams] = useSearchParams();
  const { t } = useLang();

  const [formData, setFormData] = useState({
    organizationName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    plan: searchParams.get('plan') || DEFAULT_PLAN,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  // The pricing page links here with ?plan=<planKey>. Only apply it once the
  // real plan list is in, so an unknown/stale key doesn't silently select
  // nothing in the <select>.
  useEffect(() => {
    if (plansLoading || plans.length === 0) {
      return;
    }

    const requestedPlan = searchParams.get('plan');
    const isValidPlan = requestedPlan && plans.some((plan) => plan.planKey === requestedPlan);

    setFormData((prev) => ({
      ...prev,
      plan: isValidPlan ? requestedPlan : (plans.some((plan) => plan.planKey === prev.plan) ? prev.plan : plans[0].planKey)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plansLoading, plans]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${IDENTITY_API_BASE}/v1/public/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          organizationName: '',
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          plan: 'standard',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format plan option for display
  const formatPlanOption = (plan) => {
    const memberLimit = plan.planKey === 'free' ? t('signup.upTo50') :
                        plan.planKey === 'standard' ? t('signup.upTo500') :
                        t('signup.unlimitedMembers');

    const priceText = plan.monthlyPrice === 0 ? t('signup.free') : `${plan.monthlyPrice} ${t('signup.perMonth')}`;

    return `${plan.name} - ${priceText} - ${memberLimit}`;
  };

  const nextSteps = t('signup.nextSteps');

  return (
    <div className="signup-page">
      <section className="hero">
        <div className="container">
          <h1 className="hero-title" style={{textAlign: 'center'}}>
            {t('signup.heroTitle')}
          </h1>
          <p className="hero-subtitle" style={{textAlign: 'center', maxWidth: '600px', margin: '0 auto'}}>
            {t('signup.heroSubtitle')}
          </p>
        </div>
      </section>

      <section className="section signup-section">
        <div className="container">
          <div className="signup-container">
            <div className="signup-form-wrapper">
              <h2 className="form-title">{t('signup.formTitle')}</h2>

              {submitStatus === 'success' && (
                <div className="alert alert-success">
                  {t('signup.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="alert alert-error">
                  {t('signup.error')}
                </div>
              )}

              <form className="signup-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="organizationName">{t('signup.orgName')} *</label>
                  <input
                    type="text"
                    id="organizationName"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    placeholder={t('signup.orgNamePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactName">{t('signup.contactPerson')} *</label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    placeholder={t('signup.contactPersonPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactEmail">{t('signup.email')} *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    required
                    placeholder="din@epost.no"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactPhone">{t('signup.phone')}</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="+47 123 45 678"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="plan">{t('signup.plan')} *</label>
                  <select
                    id="plan"
                    name="plan"
                    value={formData.plan}
                    onChange={handleChange}
                    required
                    disabled={plansLoading}
                  >
                    {plans.map((plan) => (
                      <option key={plan.planKey} value={plan.planKey}>
                        {formatPlanOption(plan)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('signup.messageOptional')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder={t('signup.messagePlaceholder')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('signup.sending') : t('signup.submit')}
                </button>
              </form>
            </div>

            <div className="signup-info">
              <h3>{t('signup.nextTitle')}</h3>
              <div className="step-list">
                {nextSteps.map((step, i) => (
                  <div key={i} className="step-item">
                    <div className="step-number">{i + 1}</div>
                    <div className="step-content">
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="info-box">
                <h4>{t('signup.questionsTitle')}</h4>
                <p>{t('signup.questionsContact')} <a href="mailto:kontakt@communitynord.com">kontakt@communitynord.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SignupPage;
